import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import tasksService from '../../services/tasksService';
import type { Task, RunningTasksResponse, TaskLogs, TaskExecutionLogs } from '../../services/tasksService';
import { showNotification } from './notificationSlice';

// State interface
interface TasksState {
  tasks: Task[];
  runningTasks: RunningTasksResponse | null;
  selectedTaskId: string | null;
  selectedExecutionId: string | null;
  taskLogs: TaskLogs | null;
  executionLogs: TaskExecutionLogs | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: TasksState = {
  tasks: [],
  runningTasks: null,
  selectedTaskId: null,
  selectedExecutionId: null,
  taskLogs: null,
  executionLogs: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchAllTasks = createAsyncThunk('tasks/fetchAll', async (_, { rejectWithValue }) => {
  try {
    return await tasksService.getAllTasks();
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch tasks');
  }
});

export const fetchRunningTasks = createAsyncThunk('tasks/fetchRunning', async (_, { rejectWithValue }) => {
  try {
    return await tasksService.getRunningTasks();
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch running tasks');
  }
});

export const fetchTaskLogs = createAsyncThunk(
  'tasks/fetchLogs',
  async (payload: { taskId: string; limit?: number }, { rejectWithValue }) => {
    try {
      return await tasksService.getTaskLogs(payload.taskId, payload.limit);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch task logs');
    }
  },
);

export const fetchExecutionLogs = createAsyncThunk(
  'tasks/fetchExecutionLogs',
  async (payload: { taskId: string; executionId: string }, { rejectWithValue }) => {
    try {
      return await tasksService.getTaskExecutionLogs(payload.taskId, payload.executionId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch execution logs');
    }
  },
);

export const executeTask = createAsyncThunk('tasks/execute', async (taskId: string, { dispatch, rejectWithValue }) => {
  try {
    const result = await tasksService.executeTask(taskId);
    dispatch(
      showNotification({
        message: result.message || 'Task execution triggered',
        type: 'success',
      }),
    );
    // Refresh running tasks after execution
    dispatch(fetchRunningTasks());
    return result;
  } catch (error: any) {
    dispatch(
      showNotification({
        message: error.response?.data?.message || 'Failed to execute task',
        type: 'error',
      }),
    );
    return rejectWithValue(error.response?.data?.message || 'Failed to execute task');
  }
});

export const toggleTaskEnabled = createAsyncThunk(
  'tasks/toggleEnabled',
  async (payload: { taskId: string; enabled: boolean }, { dispatch, rejectWithValue }) => {
    try {
      const result = await tasksService.setTaskEnabled(payload.taskId, payload.enabled);
      dispatch(
        showNotification({
          message: result.message || `Task ${payload.enabled ? 'enabled' : 'disabled'} successfully`,
          type: 'success',
        }),
      );
      return { ...result, taskId: payload.taskId, enabled: payload.enabled };
    } catch (error: any) {
      dispatch(
        showNotification({
          message: error.response?.data?.message || 'Failed to update task',
          type: 'error',
        }),
      );
      return rejectWithValue(error.response?.data?.message || 'Failed to update task');
    }
  },
);

// Create the slice
const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setSelectedTask: (state, action: PayloadAction<string | null>) => {
      state.selectedTaskId = action.payload;
      // Clear related data when selecting a new task
      if (state.selectedTaskId !== action.payload) {
        state.taskLogs = null;
        state.executionLogs = null;
        state.selectedExecutionId = null;
      }
    },
    setSelectedExecution: (state, action: PayloadAction<string | null>) => {
      state.selectedExecutionId = action.payload;
      // Clear execution logs when selecting a new execution
      if (state.selectedExecutionId !== action.payload) {
        state.executionLogs = null;
      }
    },
    clearTaskLogs: state => {
      state.taskLogs = null;
    },
    clearExecutionLogs: state => {
      state.executionLogs = null;
    },
  },
  extraReducers: builder => {
    builder
      // fetchAllTasks
      .addCase(fetchAllTasks.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTasks.fulfilled, (state, action) => {
        // Ensure consistent field mapping from backend to frontend
        state.tasks = action.payload.map(task => ({
          ...task,
          enabled: task.enabled ?? task.isEnabled ?? false,
          lastRunTime: task.lastRunTime ?? task.lastExecutionTime,
          nextRunTime: task.nextRunTime ?? task.nextExecutionTime,
        }));
        state.loading = false;
      })
      .addCase(fetchAllTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // fetchRunningTasks
      .addCase(fetchRunningTasks.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRunningTasks.fulfilled, (state, action) => {
        state.runningTasks = action.payload;
        state.loading = false;
      })
      .addCase(fetchRunningTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // fetchTaskLogs
      .addCase(fetchTaskLogs.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTaskLogs.fulfilled, (state, action) => {
        state.taskLogs = action.payload;
        state.loading = false;
      })
      .addCase(fetchTaskLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // fetchExecutionLogs
      .addCase(fetchExecutionLogs.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExecutionLogs.fulfilled, (state, action) => {
        state.executionLogs = action.payload;
        state.loading = false;
      })
      .addCase(fetchExecutionLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // executeTask - just handle loading states, actual changes reflected in fetchRunningTasks
      .addCase(executeTask.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(executeTask.fulfilled, state => {
        state.loading = false;
      })
      .addCase(executeTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // toggleTaskEnabled
      .addCase(toggleTaskEnabled.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleTaskEnabled.fulfilled, (state, action) => {
        const { taskId, enabled } = action.payload as { taskId: string; enabled: boolean };
        state.tasks = state.tasks.map(task => (task.id === taskId ? { ...task, enabled, isEnabled: enabled } : task));
        state.loading = false;
      })
      .addCase(toggleTaskEnabled.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions and reducer
export const { setSelectedTask, setSelectedExecution, clearTaskLogs, clearExecutionLogs } = tasksSlice.actions;

export default tasksSlice.reducer;
